import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Select,
  Table,
  message,
} from "antd";
import { ASIDeviceService } from "../../ducks/asi-devices/asi-devices";
import { ASIUserService } from "../../ducks/asi-users/asi-users.service";
import RenderGroupName from "../../components/cam/RenderGroupName";
import { AsiScheduleForm } from "../client-hub-asi/components/AsiScheduleForm";
import { useSelector } from "react-redux";
import { IUser } from "../../models/user";
import { isUserRole, resolveRole } from "../../services/roles";
import { useI18n } from "../../i18n/I18nContext";

const deps = {
  asiDevices: new ASIDeviceService(),
  asiUsers: new ASIUserService(),
};

/**
 * Shared ASI settings — engineer (admin) vs user (manager/operator) modes
 */
export const AsiDeviceSettings = ({
  asiId,
  hubId,
  asiMeta,
}: {
  asiId: string;
  hubId: string;
  asiMeta?: { description?: string; comment?: string };
}) => {
  const { t } = useI18n();
  const user = useSelector(({ UserSlice }: any) => UserSlice.user as IUser);
  const role = resolveRole(user?.role);
  const userMode = isUserRole(role);

  const [isLoadingPanel, setIsLoadingPanel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userPanel, setUserPanel] = useState<any[]>([]);
  const [draftPeople, setDraftPeople] = useState<Record<string, string[]>>({});
  const [asiPanel, setAsiPanel] = useState<any[]>([]);
  const [asiSchedule, setAsiSchedule] = useState({});
  const [asiOnlyCardOrFaceOrPassword, setAsiOnlyCardOrFaceOrPassword] =
    useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleAsiOnlyCardOrFaceOrPassword = async (e: any) => {
    setAsiOnlyCardOrFaceOrPassword(e.target.checked);
    await deps.asiDevices.updateOnlyCardOrFaceOrPassword(
      asiId,
      e.target.checked
    );
  };

  const userColumns = useMemo(
    () => [
      {
        title: t("asi.group"),
        dataIndex: "groupName",
        key: "groupName",
        width: 250,
        render: (groupName: string) => <RenderGroupName name={groupName} />,
      },
      {
        title: t("asi.people"),
        dataIndex: "people",
        key: "people",
        width: 400,
        render: (_people: any[], record: any) => {
          const { key: zoneId, allUsers = [] } = record;
          const selectedValues =
            draftPeople[zoneId] ??
            (_people?.map((p: any) => p.value || p.userId) ?? []);

          return (
            <Form>
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder={t("asi.selectPeople")}
                  options={allUsers.map((u: any) => ({
                    label: u.label || u.userName || u.userId,
                    value: u.value || u.userId,
                  }))}
                  value={selectedValues}
                  maxTagCount="responsive"
                  onChange={(selected: string[]) =>
                    setDraftPeople((prev) => ({ ...prev, [zoneId]: selected }))
                  }
                  size="middle"
                  style={{ width: "100%" }}
                  disabled={saving}
                />
              </Form.Item>
            </Form>
          );
        },
      },
    ],
    [draftPeople, saving, t]
  );

  const asiDisplayName = useMemo(() => {
    const title = asiMeta?.description?.trim();
    const comment = asiMeta?.comment?.trim();
    if (title && comment) return `${title} - ${comment}`;
    if (title) return title;
    if (comment) return comment;
    return null;
  }, [asiMeta]);

  const asiZoneColumns = [
    {
      title: t("asi.name"),
      dataIndex: "asiName",
      key: "asiName",
      width: 250,
      render: (asiName: string) => (
        <span style={{ fontWeight: 500 }}>
          {asiDisplayName || asiName}
        </span>
      ),
    },
    {
      title: userMode ? t("asi.zonesReadonly") : t("asi.zones"),
      dataIndex: "selectedZoneIds",
      key: "zones",
      width: 400,
      render: (selectedZoneIds: string[], record: any) => {
        const { zones = [], asiId: rowAsiId } = record;
        return (
          <Form>
            <Form.Item style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder={t("asi.zones")}
                options={zones.map((zone: any) => ({
                  label: zone.label || zone.groupName,
                  value: zone.value || zone.id,
                }))}
                value={selectedZoneIds}
                maxTagCount="responsive"
                onChange={(selected: string[]) =>
                  handleAssignZonesToAsi(selected, rowAsiId)
                }
                size="middle"
                style={{ width: "100%" }}
                disabled={userMode}
              />
            </Form.Item>
          </Form>
        );
      },
    },
  ];

  const handleAssignZonesToAsi = async (
    zoneIds: string[],
    targetAsiId: string
  ) => {
    if (userMode) return;
    setAsiPanel((prev: any[]) =>
      prev.map((row: any) =>
        row.asiId === targetAsiId ? { ...row, selectedZoneIds: zoneIds } : row
      )
    );
    await deps.asiDevices.bindZonesToAsi({
      asiId: targetAsiId,
      zoneIds,
    });
  };

  const handleSavePeople = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const zoneIds = Object.keys(draftPeople);
      for (const zoneId of zoneIds) {
        await deps.asiUsers.bindUsersToZone({
          asiId,
          zoneId,
          userIds: draftPeople[zoneId],
        });
      }
      message.success(t("asi.saveSuccess"), 5);
      await fetchDataSource();
    } catch (e) {
      console.log(e);
      setSaveError(t("asi.saveError"));
      message.error(t("asi.saveError"), 8);
    } finally {
      setSaving(false);
    }
  };

  const fetchDataSource = async () => {
    setIsLoadingPanel(true);
    try {
      const source: any = await deps.asiDevices.getPanelInfo({
        hubId,
        asiId,
      });
      setAsiOnlyCardOrFaceOrPassword(source?.onlyCardOrFaceOrPassword);
      const panel = (source?.userZonePanel as any[]) || [];
      setUserPanel(panel);
      const draft: Record<string, string[]> = {};
      panel.forEach((row: any) => {
        draft[row.key] =
          row.people?.map((p: any) => p.value || p.userId) ?? [];
      });
      setDraftPeople(draft);
      setAsiPanel([source?.asiZonesPanel] as any);
      setAsiSchedule(source.schedule);
    } finally {
      setIsLoadingPanel(false);
    }
  };

  useEffect(() => {
    if (asiId && hubId) {
      fetchDataSource();
    }
  }, [asiId, hubId]);

  return (
    <div>
      {(asiMeta?.description || asiMeta?.comment) && (
        <div style={{ marginBottom: 16 }}>
          {asiMeta.description && (
            <div>
              <strong>{t("asi.name")}:</strong> {asiMeta.description}
            </div>
          )}
          {asiMeta.comment && (
            <div>
              <strong>{t("asi.comment")}:</strong> {asiMeta.comment}
            </div>
          )}
        </div>
      )}

      {!userMode && asiId && (
        <Checkbox
          checked={asiOnlyCardOrFaceOrPassword}
          onChange={(e) => handleAsiOnlyCardOrFaceOrPassword(e)}
          disabled={isLoadingPanel}
        >
          Включить только карту, лицо или пароль
        </Checkbox>
      )}

      {!userMode && asiId && (
        <AsiScheduleForm asiId={asiId} schedule={asiSchedule} />
      )}

      <Table
        columns={asiZoneColumns}
        dataSource={asiPanel}
        loading={isLoadingPanel}
        style={{ marginTop: 16 }}
        pagination={false}
      />

      <Table
        dataSource={userPanel}
        columns={userColumns}
        loading={isLoadingPanel}
        style={{ marginTop: 16 }}
        pagination={false}
      />

      {saveError && (
        <Alert
          type="error"
          message={saveError}
          style={{ marginTop: 12 }}
          showIcon
          closable
          onClose={() => setSaveError(null)}
        />
      )}

      <Button
        type="primary"
        onClick={handleSavePeople}
        loading={saving}
        disabled={isLoadingPanel}
        style={{ marginTop: 16 }}
      >
        {t("asi.save")}
      </Button>
    </div>
  );
};

/** Route: /hub-asi/:id/asi-people/:id */
export const UserHubASI = () => {
  const { id } = useParams();
  const getDeviceId = () => {
    const stringURI = window.location.pathname;
    return stringURI.split("/")[2] || "";
  };
  const asiId = getDeviceId();

  if (!asiId || !id) {
    return <div>Устройство не найдено</div>;
  }

  return <AsiDeviceSettings asiId={asiId} hubId={id} />;
};

/** Route: /devices/:asiId */
export const DeviceSettingsPage = () => {
  const { t } = useI18n();
  const { asiId } = useParams();
  const [hubId, setHubId] = useState<string | null>(null);
  const [asiMeta, setAsiMeta] = useState<{
    description?: string;
    comment?: string;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!asiId) {
        setError(t("asi.notFound"));
        setLoading(false);
        return;
      }
      try {
        const device = await deps.asiDevices.getById(asiId);
        if (!device?.hubExternalId) {
          setError(t("asi.notFound"));
        } else {
          setHubId(device.hubExternalId);
          setAsiMeta({
            description: device.description,
            comment: device.comment,
          });
        }
      } catch (e) {
        console.log(e);
        setError(t("asi.notFound"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [asiId, t]);

  if (loading) {
    return <div>{t("asi.loading")}</div>;
  }

  if (error || !asiId || !hubId) {
    return <div>{error || t("asi.notFound")}</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>{t("asi.settings")}</h2>
      <AsiDeviceSettings asiId={asiId} hubId={hubId} asiMeta={asiMeta} />
    </div>
  );
};

export default UserHubASI;
